import { RegisterableHotkey } from '@tanstack/react-hotkeys';
import { ClassValue, clsx } from 'clsx';
import { ReactNode, useEffect, useId, useRef } from 'react';
import { KeyCaps } from '@shared/components/KeyCaps';
import { View, ViewProps } from '@shared/components/View';
import { useShowHotkeyTooltips } from '@shared/util/hotkeys';
import { getController } from '@shared/util/timeout';
import styles from './AnchoredTooltip.module.css';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface AnchoredTooltipProps extends ViewProps {
  anchorName?: string;
  tooltip?: ReactNode;
  tooltipClassName?: ClassValue;
  /** Renders a key cap hint beside the tooltip text, when hints are enabled. */
  hotkey?: RegisterableHotkey | null;
  position?: TooltipPosition;
  disabled?: boolean;
}

const defaultTooltipAnchorName = '--tooltip-anchor';

export function AnchoredTooltip({
  anchorName = defaultTooltipAnchorName,
  className,
  children,
  tooltip,
  tooltipClassName,
  hotkey,
  position = 'top',
  disabled = false,
  ...rest
}: AnchoredTooltipProps) {
  const ref = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();
  const [showHotkeyTooltips] = useShowHotkeyTooltips();
  const showKeyCaps = !!hotkey && showHotkeyTooltips;

  useEffect(() => {
    if (disabled) {
      return;
    }

    const firstChild = ref.current?.firstChild;
    const tooltipEl = tooltipRef.current;
    if (!(firstChild instanceof HTMLElement) || !tooltipEl) {
      return;
    }

    const { signal, abort } = getController();

    const show = () => {
      firstChild.setAttribute('aria-describedby', tooltipId);
      tooltipEl.showPopover();
    };
    const hide = () => {
      firstChild.removeAttribute('aria-describedby');
      tooltipEl.hidePopover();
    };

    firstChild.style.anchorName = anchorName;
    firstChild.addEventListener('mouseenter', show, { signal });
    firstChild.addEventListener('mouseleave', hide, { signal });
    return () => {
      abort();
      hide();
    };
  }, [disabled, anchorName, tooltipId]);

  return (
    <View
      {...rest}
      ref={ref}
      className={[styles.anchoredTooltip, className]}
      data-position={position}
    >
      {children}
      {(tooltip || showKeyCaps) && !disabled && (
        <div
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          className={clsx([styles.tooltip, tooltipClassName])}
          style={{ positionAnchor: anchorName }}
          popover="manual"
          data-theme="default"
        >
          {tooltip}
          {showKeyCaps && (
            <View center className={styles.keycaps}>
              <KeyCaps hotkey={hotkey} />
            </View>
          )}
        </div>
      )}
    </View>
  );
}
